import React, { useContext, useEffect, useState } from "react";
import {
  SectionWrapper,
  LeftContent,
  Title,
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
} from "./HeroSection.styles";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import AuthModal from "../modal/AuthModal";
import { AnimatePresence, motion } from "framer-motion";
import {
  Briefcase,
  MessageSquare,
  PlusCircle,
  Users,
  Play,
  Wallet,
} from "lucide-react";

const HeroSection = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [pairIndex, setPairIndex] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");

  const ctaTexts = [
    {
      title: "Got a Coding Issue?",
      description: "Post your issue and get it solved by other developers.",
    },
    {
      title: "Are you a Developer?",
      description: "Earn by solving code issues posted by others.",
    },
    {
      title: "Looking for Freelance Projects?",
      description: "Work with real clients and grow your portfolio.",
    },
    {
      title: "Join Expert Developers",
      description: "Collaborate, earn, and upskill with our dev community.",
    },
  ];

  // 🔹 Typewriter effect for dynamic headline
  useEffect(() => {
    const currentLine =
      lineIndex === 0
        ? ctaTexts[pairIndex].title
        : ctaTexts[pairIndex].description;

    let charIndex = 0;
    const interval = setInterval(() => {
      setDisplayedText(currentLine.slice(0, charIndex + 1));
      charIndex++;

      if (charIndex === currentLine.length) {
        clearInterval(interval);
        setTimeout(() => {
          if (lineIndex === 0) setLineIndex(1);
          else {
            setLineIndex(0);
            setPairIndex((prev) => (prev + 1) % ctaTexts.length);
          }
        }, 1500);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [pairIndex, lineIndex]);

  const handleProtectedRoute = (path) => {
    if (user) navigate(path);
    else setModalOpen(true);
  };

  return (
    <SectionWrapper>
      <LeftContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={pairIndex + "-" + lineIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <Title>{displayedText}</Title>
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2, type: "spring", stiffness: 100 }}
        >
          <ButtonGroup>
            {/* 🔹 Post an Issue */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <PrimaryButton
                onClick={() => handleProtectedRoute("/post-code-issue")}
              >
                <PlusCircle size={20} />
                Post an Issue
              </PrimaryButton>
            </motion.div>

            {/* 🔹 Earn by Solving */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <SecondaryButton
                onClick={() => handleProtectedRoute("/code-issues")}
              >
                <MessageSquare size={20} />
                Earn by Solving
              </SecondaryButton>
            </motion.div>

            {/* 🔹 Start Freelancing */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <SecondaryButton
                onClick={() => handleProtectedRoute("/freelance-projects")}
              >
                <Briefcase size={20} />
                Start Freelancing
              </SecondaryButton>
            </motion.div>

            {/* 🔹 Explore Referral Jobs */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <SecondaryButton
                onClick={() => handleProtectedRoute("/referral-jobs")}
              >
                <Wallet size={20} />
                Explore Referral Jobs
              </SecondaryButton>
            </motion.div>

            {/* 🔹 Join Developer Community (WhatsApp) */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <SecondaryButton
                as="a"
                href="https://chat.whatsapp.com/DJ99t0ZZuuUHELAht7lNh9"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Users size={20} />
                Join Developer Community
              </SecondaryButton>
            </motion.div>

            {/* 🔹 Watch Demo / Learn More */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            >
              <SecondaryButton onClick={() => navigate("/how-this-works")}>
                <Play size={20} />
                How This Works
              </SecondaryButton>
            </motion.div>
          </ButtonGroup>
        </motion.div>
      </LeftContent>

      {/* 🔹 Auth Modal */}
      <AuthModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </SectionWrapper>
  );
};

export default HeroSection;
